import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  SkyBody1,
  SkyButton,
  SkyPopover,
  SkyDialog,
  SkyInput,
  SkyBadge,
  SkyDivider,
  type SkyBadgeColor,
} from "@skypointcloud/skypoint-ui";
import { Plus, Plug, ChevronDown, Settings2 } from "lucide-react";

import { ROUTES } from "../../../components/shared/constant";
import { getInstanceIdFromUrl } from "../../../utils/instanceId";
import { useNotification } from "../../../common/hooks/useNotification";
import {
  useGetMcpServersQuery,
  useGetMcpServerToolsQuery,
  useAuthorizeMcpAccountMutation,
  useConnectMcpAccountMutation,
  useDisconnectMcpAccountMutation,
  type McpServer,
} from "../../../store/api/crmMcpApi";

/**
 * The composer's "+" menu, and the Connectors submenu inside it.
 *
 * This is the EVERYDAY surface for connectors. The management screen is where a
 * manager registers a server once; this is where a counselor connects their own
 * account and sees, at a glance, whether skyAgent can actually reach anything on
 * their behalf right now.
 *
 * ## The two states, and why they must never look alike
 *
 * A row carries two independent facts:
 *
 *   `enabled` + `status`  — the SERVER. Customer-level, identical for everyone.
 *   `connection.connected` — ME. My own account, mine alone.
 *
 * So a healthy server that I have simply not signed into is `status: "connected",
 * connected: false`, and it must read as an invitation, not a fault. The failure
 * this guards is rendering "nobody has connected yet" the same as "the server is
 * broken" — the first is one click from working, the second is not fixable by the
 * person looking at it. They get different words, different colours, and
 * different actions.
 *
 * ## Absence is the gate
 *
 * With the capability off the server answers `{enabled: false}` and this whole
 * entry is NOT RENDERED — no greyed row, no "connectors are disabled" note. A
 * disabled surface that announces itself teaches people to ask for something the
 * tenant has decided against, and in the agent's own prompt the same pattern has
 * already caused it to narrate capabilities it did not have.
 *
 * ## What the copy may and may not say
 *
 * Every sentence here has to be one the code keeps:
 *
 *  - "the other system decides what you can see" is TRUE — the remote enforces
 *    that person's own permissions and we never re-implement them.
 *  - The interruption warning is shown from REAL per-tool permissions, fetched
 *    for that server at the moment you connect, never asserted generically.
 *  - Nothing here claims the token is recoverable, because it is not. It is
 *    write-only: no endpoint returns it, and there is no field in this file that
 *    could display one.
 */

/** How a row reads. Derived from the two states — never from one. */
type RowState = "off" | "checking" | "unreachable" | "reconnect" | "connect" | "connected";

export function rowState(server: McpServer): RowState {
  // Order matters, and it is server-first for a reason: a problem with the SERVER
  // is true for everybody and cannot be fixed by the person reading this, so it
  // outranks anything about their own sign-in.
  //
  // `enabled` first of all — a switched-off server has `status: null`, and reading
  // null as "unreachable" would report an administrator's decision as a fault.
  if (!server.enabled) return "off";
  if (server.status === "connecting") return "checking";
  if (server.status === "error" || server.status === null) return "unreachable";

  // The server is fine. Now the part that is about ME.
  //
  // `needsReconnect` is read from the CONNECTION, not from the server status, and
  // that is the whole correction: it was briefly server-level, which meant one
  // colleague's expired sign-in turned the row amber for everyone the connector
  // still worked for. A per-person fact has to come off the per-person object or
  // it is not a per-person fact.
  if (server.connection.needsReconnect) return "reconnect";
  return server.connection.connected ? "connected" : "connect";
}

/**
 * Wording aligned with skyCommand's connector surface (their `STATUS_META` plus
 * their caller-needs-sign-in pill), so the two products describe the same states
 * the same way.
 *
 * The `connect` label is "Not connected" rather than "Connect your account" —
 * their distinction, and it is right: the STATE is "not connected", the ACTION is
 * "Connect your account". Using one string for both made the state pill read like
 * a second button.
 *
 * ⚠️ WE ALWAYS DO PER-USER CREDENTIALS. skyCommand has an `identityMode` and only
 * applies its sign-in state when that is `delegated`; we have no shared mode, so
 * every healthy server a person has not signed into is `connect` for them. Do not
 * "align with skyCommand" by adding a mode check here — there is nothing to
 * check, and the remote enforcing each individual's own permissions is the whole
 * reason our design has no shared token.
 */
const STATE_LABEL: Record<RowState, string> = {
  off: "Disabled",
  checking: "Pending",
  unreachable: "Needs attention",
  reconnect: "Sign in again",
  connect: "Not connected",
  connected: "Connected",
};

const STATE_COLOR: Record<RowState, SkyBadgeColor> = {
  off: "subtle",
  checking: "informative",
  unreachable: "danger",
  // Amber, not red: this is something the reader can fix in ten seconds, and the
  // colour should say "your turn", not "it is broken".
  reconnect: "warning",
  connect: "warning",
  connected: "success",
};

/**
 * The one-line explanation under each state.
 *
 * `unreachable` deliberately says connecting will not help. Without it, a person
 * looking at a broken server sees a connect-shaped row and spends their afternoon
 * pasting a token that was never the problem.
 */
const STATE_HINT: Record<RowState, string> = {
  off: "An administrator has switched this off.",
  checking: "Still checking whether it answers.",
  unreachable: "skyAgent cannot reach it. Connecting your account will not fix this.",
  reconnect: "Your sign-in has expired or was refused. Connecting again will fix it.",
  connect: "Sign in once and skyAgent can use it while it answers you.",
  connected: "skyAgent can use this while it answers you.",
};

/**
 * What this system will NOT do, built from its real stored tool permissions.
 *
 * Shown inside the connect dialog rather than in the menu, because that is the
 * moment a person is deciding — and because it needs a per-server fetch that would
 * otherwise cost one request per row just to render a menu.
 *
 * ## It used to warn about `ask`, and both halves of that were wrong
 *
 * `ask` is retired: the seeder no longer writes it, the validator no longer accepts
 * it, and V196 clears the rows. So the branch was dead — it would have rendered
 * "None of this system's tools will stop to ask you" forever.
 *
 * Worse, the sentence it rendered when a row DID survive said skyAgent "will pause
 * mid-answer and wait for you". It never paused. `buildMcpTools` is called with no
 * approver, so `requestApproval` returns `no_channel` and the call is DENIED
 * outright. A dialog that promises a wait before an immediate refusal is worse than
 * no dialog.
 *
 * What is left is the half that is still true and still has a writer: a tool an
 * operator switched OFF. Renders nothing while the fetch is in flight or has
 * failed — a promise about what a system will not do must not be made on absent
 * data.
 */
const SwitchedOffNote = ({ instanceId, serverId }: { instanceId: string; serverId: string }) => {
  const { data, isLoading, isError } = useGetMcpServerToolsQuery({ instanceId, id: serverId });
  if (isLoading || isError || !data?.tools) return null;

  const never = data.tools.filter((t) => t.permission === "never").length;
  if (never === 0) return null;

  return (
    <SkyBody1
      className="block"
      data-testid="connect-interruptions"
    >
      {never === 1 ? "One tool is" : `${never} tools are`} switched off and will never run.
    </SkyBody1>
  );
};

const ConnectorsMenu = () => {
  const instanceId = getInstanceIdFromUrl() || "";
  const navigate = useNavigate();
  const { addNotification } = useNotification();

  /**
   * The connect poll's interval id.
   *
   * This poll is NEW in practice, not new in code: `noopener` used to make
   * `window.open` return null, so the handler returned at `if (!win)` and the
   * interval below was unreachable. Fixing the pop-up made it run for the first
   * time — and an interval whose only exit is `win.closed` outlives the component.
   * Navigate away from the rail with the consent window still open and it keeps
   * firing `refetch()` against a torn-down hook for the life of the tab; click
   * Connect three times and three of them run at once.
   */
  const pollRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (pollRef.current !== null) window.clearInterval(pollRef.current);
    },
    [],
  );

  const [plusOpen, setPlusOpen] = useState(false);
  const [connectorsOpen, setConnectorsOpen] = useState(false);
  const [connecting, setConnecting] = useState<McpServer | null>(null);
  const [token, setToken] = useState("");
  const [connectError, setConnectError] = useState<string | null>(null);
  /**
   * WHY this person is being asked to paste a token — a different fact from what
   * went wrong when they submitted one, and it must not share a field with it.
   * Folded together, a rejected token would overwrite the explanation of why the
   * dialog appeared, and a stale explanation would read as an error.
   */
  const [pasteReason, setPasteReason] = useState<string | null>(null);

  const { data, isError, refetch } = useGetMcpServersQuery({ instanceId }, { skip: !instanceId });
  const [authorizeAccount, { isLoading: isAuthorizing }] = useAuthorizeMcpAccountMutation();
  const [connectAccount, { isLoading: isConnecting }] = useConnectMcpAccountMutation();
  const [disconnectAccount] = useDisconnectMcpAccountMutation();

  const servers = data?.servers ?? [];
  // Absence is the gate. Not a disabled entry — nothing at all.
  const capabilityOff = data?.enabled === false;

  const closeAll = () => {
    setConnectorsOpen(false);
    setPlusOpen(false);
  };

  /** The paste-a-token fallback. Only reached when OAuth is not available. */
  const openPasteDialog = (server: McpServer, reason?: string) => {
    setConnecting(server);
    setToken("");
    setConnectError(null);
    setPasteReason(reason ?? null);
    closeAll();
  };

  /**
   * "Connect your account" — THE REAL SIGN-IN.
   *
   * Sends the person to the connected system's own consent page, which is what
   * signing in to something actually looks like. Pasting an access token is the
   * fallback for a server that publishes no OAuth discovery, not the default: a
   * button called "Connect your account" that instead asks a counselor to go and
   * mint a token in another product's settings is a thing that ships and then
   * gets apologised for.
   *
   * The two halves of this existed for a whole build and were never joined — the
   * authorize route was built, tested and mounted, and this button called the
   * fallback. Nothing failed, because the fallback works. There was no error to
   * notice, only a worse product.
   *
   * ## Why it attempts rather than checking first
   *
   * `authKind` says which a server wants, but the list endpoint does not send it
   * yet — the column exists and is simply not selected. So `undefined` means
   * "not known", and treating that as bearer would keep this path invisible for
   * every server. Known-bearer skips straight to paste; anything else tries
   * OAuth and falls back on refusal, which is correct either way and gets
   * cheaper the day the field arrives.
   */
  const startConnect = async (server: McpServer) => {
    if (server.authKind === "bearer") {
      openPasteDialog(server);
      return;
    }

    // ── OPENED SYNCHRONOUSLY, AND WITHOUT `noopener`. Both halves matter. ─────
    //
    // This reported as "browser permission error" and was neither: the consent
    // window opened every time AND the blocked-popup branch fired every time.
    //
    //  1. `noopener` in the FEATURES string makes `window.open` return `null` on
    //     success — there is no handle to give back. `if (!win)` therefore read
    //     "blocked" on a window the user could see loading in front of them. The
    //     same trap is written up in `FlyerPreview.tsx`; it cost a bug twice.
    //     `opener` is severed by hand below instead, so the destination still
    //     cannot reach back into this tab.
    //  2. Opening AFTER `await authorizeAccount(...)` is no longer coupled to the
    //     click, so a browser is entitled to block it for real. The handle is
    //     taken here, inside the gesture, and pointed at the URL once it lands.
    // NAMED, not `_blank`: the navigation below targets this window by name, which
    // is what lets it carry a referrer policy (see there). Per-server, so two
    // connectors cannot fight over one window.
    const target = `mcp_oauth_${server.id}`;
    const win = window.open("", target, "width=560,height=720");
    if (win) win.opener = null;

    try {
      const { authorizeUrl } = await authorizeAccount({
        instanceId,
        serverId: server.id,
      }).unwrap();

      closeAll();

      if (!win) {
        // A GENUINE block now — the synchronous open was refused. Worth saying,
        // because it is the one failure the person can fix themselves.
        addNotification({
          type: "ERROR",
          message: `Allow pop-ups for this site, then connect ${server.name} again.`,
        });
        return;
      }

      // ── NAVIGATED BY AN ANCHOR, not `win.location.href = url`. ───────────────
      //
      // The features string lost `noreferrer` along with `noopener`, and only
      // `noopener` was replaced (by severing `opener` above). They are independent:
      // assigning `location.href` is a navigation initiated by THIS document, so
      // under the default policy the connected system's authorize endpoint — any
      // customer-registered MCP server URL, not just skyNote — receives a `Referer`
      // carrying our origin and the instance-scoped path. Tenant URLs are sensitive
      // per the repo's data-handling rule.
      //
      // `location.href` cannot carry a referrer policy; an anchor can.
      // `referrerPolicy` alone, WITHOUT `rel="noreferrer"` — the rel would force a
      // fresh browsing context, losing the named window and with it the handle the
      // close-poll needs.
      const nav = document.createElement("a");
      nav.href = authorizeUrl;
      nav.target = target;
      nav.referrerPolicy = "no-referrer";
      nav.style.display = "none";
      document.body.appendChild(nav);
      nav.click();
      nav.remove();

      // The callback renders its own page and does NOT message the opener, so
      // there is no completion event to await. Watching the window close is the
      // available signal, and the refetch is what actually updates the row —
      // correct whether they finished, cancelled, or just closed the tab.
      // Any poll from a previous click is dead the moment a new one starts.
      if (pollRef.current !== null) window.clearInterval(pollRef.current);
      const poll = window.setInterval(() => {
        if (!win.closed) return;
        window.clearInterval(poll);
        if (pollRef.current === poll) pollRef.current = null;
        void refetch();
      }, 500);
      pollRef.current = poll;
    } catch (error) {
      // The blank window was opened before we knew the request would succeed, so
      // every failure path below has to clean it up. Leaving it would strand an
      // empty pop-up on screen next to an error explaining something else.
      win?.close();
      const body = (error as { data?: { error?: string; code?: string } })?.data;

      // REDIRECT_URI_NOT_CONFIGURED is OUR deployment missing a setting, not a
      // fault in the connector. Falling through to "paste a token" would send a
      // counselor to do work in another product that cannot possibly help.
      if (body?.code === "REDIRECT_URI_NOT_CONFIGURED") {
        addNotification({
          type: "ERROR",
          message: "Signing in is not set up on this environment yet. Ask an administrator.",
        });
        return;
      }

      // Anything else means this server cannot do the hosted flow. THAT is what
      // the paste path is for, and the dialog says why it is being offered
      // rather than presenting itself as the normal way in.
      openPasteDialog(
        server,
        `${server.name} does not offer the usual sign-in, so it needs an access token instead.`,
      );
    }
  };

  /**
   * Returns TRUE when the dialog may close.
   *
   * ## Why this returns a boolean instead of just doing the work
   *
   * SkyDialog closes on its own as soon as the primary action is clicked. Run
   * from `primaryAction.onClick`, a FAILED connect would set the error message
   * into a dialog that had already gone — the person would click Connect, watch
   * everything vanish, and be given no reason at all. A test caught exactly that.
   *
   * So the submit runs from `onBeforeClose`, which can veto the close: the dialog
   * stays open on failure with the reason in it, and closes only on success.
   */
  const submitConnect = async (): Promise<boolean> => {
    if (!connecting) return true;
    if (!token.trim()) {
      setConnectError("Paste the token first.");
      return false;
    }
    setConnectError(null);
    try {
      await connectAccount({
        instanceId,
        serverId: connecting.id,
        token: token.trim(),
      }).unwrap();
      addNotification({ type: "SUCCESS", message: `Connected to ${connecting.name}.` });
      // Cleared on the way out as well as on the way in. The value is write-only
      // everywhere else; leaving it in React state after the dialog closes would
      // be the one place in the app that still held it.
      setToken("");
      setConnecting(null);
      return true;
    } catch (error) {
      // Rendered inline in the form, and deliberately NOT passed through any
      // formatter that might echo the request body. The server's message is
      // written to be safe to show; the fallback says nothing about the value.
      setConnectError(
        (error as { data?: { error?: string } })?.data?.error ??
          "That did not work. Check the token and try again.",
      );
      return false;
    }
  };

  const disconnect = async (server: McpServer) => {
    try {
      await disconnectAccount({ instanceId, serverId: server.id }).unwrap();
      addNotification({ type: "SUCCESS", message: `Disconnected from ${server.name}.` });
    } catch {
      addNotification({ type: "ERROR", message: "Could not disconnect. Try again." });
    }
  };

  const goToManage = () => {
    closeAll();
    navigate(`${ROUTES.CRM_MCP_CONNECTORS}/${instanceId}`);
  };

  if (capabilityOff) return null;

  const connectedCount = servers.filter((s) => rowState(s) === "connected").length;

  return (
    <>
      <SkyPopover
        open={plusOpen}
        onOpenChange={setPlusOpen}
        placement="top-start"
        testId="composer-plus"
        trigger={
          <SkyButton
            appearance="transparent"
            size="small"
            aria-label="Add to this conversation"
            title="Add to this conversation"
            className="px-1.5 text-muted-foreground"
            onClick={() => setPlusOpen((o) => !o)}
          >
            <Plus
              className="h-4 w-4"
              aria-hidden="true"
            />
          </SkyButton>
        }
      >
        <div className="w-80 p-1">
          {/*
            ONE POPOVER, NOT TWO. This used to be a nested SkyPopover, and the
            nesting silently broke every action inside it.
            ────────────────────────────────────────────────────────────────
            SkyPopover dismisses on a document-level `mousedown` whenever the
            target is outside its own trigger and content. A nested popover
            renders its content in a PORTAL, so that content is not a DOM
            descendant of the outer popover's content — and the outer popover
            therefore read every press inside the submenu as "outside".

            Pressing "Connect your account" went: mousedown → outer popover
            closes → the whole subtree unmounts → the button is detached before
            `click` is dispatched → its handler never runs. The menu shut and
            nothing else happened: no dialog, no request, not even an error.

            It passed every test, because `fireEvent.click` dispatches ONLY a
            click. No `mousedown`, no dismissal, no bug. The reproduction needed
            a full pointer sequence, and `__tests__/ConnectorsMenu.test.tsx` now
            uses one for exactly this reason.

            Flat also matches the reference implementation, which renders its
            connector rows inline in the "+" menu rather than behind a submenu.
            Do not reintroduce a nested popover here.
          */}
          <button
            type="button"
            data-testid="composer-connectors-entry"
            aria-expanded={connectorsOpen}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-muted"
            onClick={() => setConnectorsOpen((o) => !o)}
          >
            <Plug
              className="h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <span className="flex-1">Connectors</span>
            {/* The count is CONNECTED-for-me, not registered-for-the-customer.
                A "3" that meant "three exist, none of them yours" would be the
                same conflation this whole component exists to avoid. */}
            {connectedCount > 0 && (
              <SkyBadge
                appearance="tint"
                color="success"
                size="small"
              >
                {connectedCount}
              </SkyBadge>
            )}
            <ChevronDown
              className={`h-4 w-4 text-muted-foreground transition-transform ${
                connectorsOpen ? "" : "-rotate-90"
              }`}
              aria-hidden="true"
            />
          </button>

          {connectorsOpen && (
            <div
              className="p-2"
              data-testid="connectors-submenu"
            >
              {/* NO HEADING, NO PARAGRAPH.
                  This opened with "Connectors" as a heading inside a panel whose
                  trigger already says Connectors, then three lines of
                  explanation before the first row. That copy is right on a
                  surface you meet once and wrong on one you open every day: read
                  once, then in the way forever.

                  The explanation lives on the manage screen, which is where
                  someone is when they are deciding rather than doing. This is a
                  list with actions.

                  Not the same call as Drafts, where "written and not sent" was
                  PROMOTED into a bar — that sentence prevents a specific harm and
                  has to be read every time. Nothing here is load-bearing in that
                  way; it is orientation. */}
              {isError ? (
                /* COULD NOT READ, not NOTHING TO READ. Falling through to "none
                   set up yet" tells a counselor their team has no connectors
                   when the truth is we could not ask — so they stop looking for
                   the one that is actually there. */
                <div className="px-1 py-3">
                  <SkyBody1
                    className="block"
                    data-testid="connectors-load-error"
                  >
                    Could not check your connectors just now.
                  </SkyBody1>
                  <SkyBody1 className="mt-1 block text-sm">
                    This is not the same as having none. Try again in a moment.
                  </SkyBody1>
                </div>
              ) : servers.length === 0 ? (
                <div className="px-1 py-3">
                  <SkyBody1 className="block">No connectors have been set up yet.</SkyBody1>
                  {/* Names the PLACE, not the button. "A manager adds them under
                      Manage connectors" points at a control the reader is already
                      looking at; where they actually have to go is the sidebar,
                      and it is filed there under a different word. */}
                  <SkyBody1 className="mt-1 block text-sm">
                    A manager adds them under skyBrain → Integrations.
                  </SkyBody1>
                </div>
              ) : (
                <ul className="flex flex-col gap-1">
                  {servers.map((server) => {
                    const state = rowState(server);
                    return (
                      <li
                        key={server.id}
                        data-testid="connector-row"
                        data-state={state}
                        className="rounded-md px-1 py-2"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <SkyBody1 className="truncate font-medium">{server.name}</SkyBody1>
                          <SkyBadge
                            appearance="tint"
                            size="small"
                            color={STATE_COLOR[state]}
                          >
                            {STATE_LABEL[state]}
                          </SkyBadge>
                        </div>
                        {/* The hint stays only where it CHANGES WHAT YOU DO:
                            "connecting will not fix this" on an unreachable
                            server, and "signing in again will" on a lapsed one.
                            Connected and not-connected already say everything
                            from the badge and the button beside it, so a
                            sentence there is a third way of saying the same
                            thing. */}
                        {(state === "unreachable" || state === "reconnect" || state === "off") && (
                          <SkyBody1 className="mt-0.5 block text-sm">{STATE_HINT[state]}</SkyBody1>
                        )}
                        {/* `reconnect` gets the SAME action, because signing in
                            again is genuinely the fix — offering it only for a
                            first connection would leave someone whose token
                            expired staring at an error with no way out. */}
                        {(state === "connect" || state === "reconnect") && (
                          <SkyButton
                            appearance="secondary"
                            size="small"
                            className="mt-1.5"
                            // Discovery and dynamic client registration happen
                            // before a URL comes back, so this is a round trip
                            // to a third party, not an instant one. Unlabelled,
                            // it reads as a dead button — which is exactly how
                            // the last defect on this control presented.
                            disabled={isAuthorizing}
                            onClick={() => void startConnect(server)}
                          >
                            {isAuthorizing
                              ? "Opening sign-in…"
                              : state === "reconnect"
                                ? "Sign in again"
                                : "Connect your account"}
                          </SkyButton>
                        )}
                        {/* A BUTTON, danger, aligned right with the other row
                            actions. It was `subtle`, which renders as a bare
                            text link — so the one destructive action on the row
                            looked lighter than the badge above it. */}
                        {state === "connected" && (
                          <div className="mt-1.5 flex justify-end">
                            {/* `outline` + the destructive token, which is how
                                this app already spells a destructive button
                                (see ResidentDetailPage's "Move out"). SkyButton
                                has no `danger` appearance — checked, not
                                assumed — so inventing one here would diverge
                                from every other destructive control. */}
                            <SkyButton
                              appearance="outline"
                              size="small"
                              className="text-destructive"
                              onClick={() => disconnect(server)}
                            >
                              Disconnect
                            </SkyButton>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              <SkyDivider className="my-2" />

              <button
                type="button"
                data-testid="manage-connectors"
                className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-muted"
                onClick={goToManage}
              >
                <Settings2
                  className="h-4 w-4 text-muted-foreground"
                  aria-hidden="true"
                />
                Manage connectors
              </button>
            </div>
          )}
        </div>
      </SkyPopover>

      <SkyDialog
        open={connecting !== null}
        onOpenChange={(o) => {
          if (!o) {
            setConnecting(null);
            setToken("");
            setConnectError(null);
            setPasteReason(null);
          }
        }}
        title={connecting ? `Connect your ${connecting.name} account` : "Connect your account"}
        // The submit lives HERE, not on primaryAction.onClick, because only
        // onBeforeClose can keep the dialog open when the connect fails. See
        // submitConnect.
        onBeforeClose={(reason) => (reason === "primaryAction" ? submitConnect() : true)}
        primaryAction={{
          label: isConnecting ? "Connecting…" : "Connect",
          // Deliberately empty: clicking triggers the dialog's own close flow,
          // which runs onBeforeClose above. Doing the work in both places would
          // send the token twice.
          onClick: () => {},
        }}
        secondaryAction={{ label: "Cancel", onClick: () => setConnecting(null) }}
      >
        <div className="flex flex-col gap-3">
          {/* THIS DIALOG IS THE FALLBACK, and it says so.
              Most servers sign a person in on their own consent page and never
              reach here. Arriving with no explanation would make pasting a token
              look like the normal way in — which is how the hosted flow went
              unnoticed for a whole build in the first place. */}
          <SkyBody1 data-testid="paste-fallback-reason">
            {pasteReason ??
              `${connecting?.name ?? "This system"} does not offer the usual sign-in, so it needs an access token instead.`}
          </SkyBody1>
          <SkyBody1>
            Create one in {connecting?.name} itself, under its own settings, and paste it here.
          </SkyBody1>

          <SkyInput
            label="Access token"
            // A password field, and it means it: this value is sent once and can
            // never be read back — no endpoint returns it, and nothing in this app
            // will ever show it to you again. If you lose it, you make a new one.
            type="password"
            autoComplete="off"
            value={token}
            placeholder="Paste your token"
            onChange={(e) => {
              setToken(e.target.value);
              setConnectError(null);
            }}
          />

          {connectError && (
            <SkyBody1
              data-testid="connect-error"
              className="block text-destructive"
            >
              {connectError}
            </SkyBody1>
          )}

          <SkyDivider />

          <SkyBody1 className="block">
            This is your own sign-in, not the whole team&apos;s. {connecting?.name} decides what
            your account may see, and skyAgent will only ever get what you could already see there.
          </SkyBody1>

          {connecting && (
            <SwitchedOffNote
              instanceId={instanceId}
              serverId={connecting.id}
            />
          )}
        </div>
      </SkyDialog>
    </>
  );
};

export default ConnectorsMenu;
